const CONTEXT_KEYS = ['token', 'language', 'activityId', 'deviceId']
const GLOBAL_CONTEXT_KEY = '__DG_CAMPAIGN_CONTEXT__'
const STORAGE_KEYS = {
  activityId: 'dg_campaign_activity_id',
  deviceId: 'dg_campaign_device_id'
}

const memoryState = {
  token: '',
  language: '',
  activityId: '',
  deviceId: ''
}

// 统一去除首尾空格，确保上下文数据稳定
const sanitizeString = (value) => (typeof value === 'string' ? value.trim() : '')

// 安全获取 localStorage，避免无窗口环境报错
const getStorage = () => {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    return window.localStorage || null
  } catch (error) {
    console.warn('localStorage 不可用:', error)
    return null
  }
}

// 根据 key 提供对应的兜底值，保证关键字段在刷新后可恢复
const getFallbackValue = (key) => {
  const storage = getStorage()
  switch (key) {
    case 'token':
      return storage?.getItem('token') || ''
    case 'language':
      return storage?.getItem('lang') || ''
    case 'activityId':
      return (
        storage?.getItem(STORAGE_KEYS.activityId) ||
        (import.meta.env.VITE_DG_CAMPAIGN_ID || '')
      )
    case 'deviceId':
      return storage?.getItem(STORAGE_KEYS.deviceId) || ''
    default:
      return ''
  }
}

// 仅对需要持久化的字段同步到 localStorage
const persistIfNeeded = (key, value) => {
  if (!STORAGE_KEYS[key]) {
    return
  }
  const storage = getStorage()
  if (!storage) {
    return
  }
  if (!value) {
    storage.removeItem(STORAGE_KEYS[key])
    return
  }
  storage.setItem(STORAGE_KEYS[key], value)
}

// 获取活动上下文信息，为请求拦截器提供基础元数据
export const getCampaignContext = () => {
  const globalContext =
    typeof window !== 'undefined' ? window[GLOBAL_CONTEXT_KEY] || {} : {}
  const result = {}

  CONTEXT_KEYS.forEach((key) => {
    const value =
      globalContext[key] ??
      memoryState[key] ??
      getFallbackValue(key)
    result[key] = sanitizeString(value)
  })

  return result
}

// 更新活动上下文（通常由 JSBridge 回调调用）
export const setCampaignContext = (partial = {}) => {
  let changed = false

  CONTEXT_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(partial, key)) {
      const sanitized = sanitizeString(partial[key])
      if (memoryState[key] !== sanitized) {
        memoryState[key] = sanitized
        persistIfNeeded(key, sanitized)
        changed = true
      }
    }
  })

  if (changed && typeof window !== 'undefined') {
    window[GLOBAL_CONTEXT_KEY] = {
      ...(window[GLOBAL_CONTEXT_KEY] || {}),
      ...memoryState
    }
  }
}

// 清空活动上下文，便于登出或切换账号时重置
export const clearCampaignContext = () => {
  CONTEXT_KEYS.forEach((key) => {
    memoryState[key] = ''
    persistIfNeeded(key, '')
  })
  if (typeof window !== 'undefined' && window[GLOBAL_CONTEXT_KEY]) {
    window[GLOBAL_CONTEXT_KEY] = { ...memoryState }
  }
}

export const __internals__ = {
  sanitizeString,
  getFallbackValue
}
